import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import { get_ticket_detail_api } from "../../DAL/SupportTickets";
import { useSnackbar } from "notistack";
import { useContent } from "../../hooks/useContent";
import PageLoader from "../../Components/GeneralComponents/PageLoader";
import moment from "moment";

const TicketDetail = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { ticket_id } = useParams();
  const { setPageHeading } = useContent();
  const [ticket, setTicket] = useState(null);
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  useEffect(() => {
    setPageHeading("Support Ticket Details");
    loadTicketDetail();
  }, [ticket_id]);

  const loadTicketDetail = async () => {
    setIsLoadingPage(true);
    try {
      const result = await get_ticket_detail_api(ticket_id);
      if (result.code === 200) {
        setTicket(result.ticket);
      } else {
        enqueueSnackbar(`${result.message}`, { variant: "error" });
        navigate("/support-tickets");
      }
    } catch (error) {
      enqueueSnackbar("Error loading ticket details", { variant: "error" });
      navigate("/support-tickets");
    }
    setIsLoadingPage(false);
  };

  const handleBack = () => navigate("/support-tickets");
  const handleEdit = () => navigate(`/support-tickets/edit-ticket/${ticket_id}`);

  const getStatusColor = (status) => {
    const statusColors = {
      OPEN: { bg: "hsl(210 100% 51% / 0.15)", color: "#137fec", icon: "mdi:circle-outline" },
      "IN-PROGRESS": { bg: "hsl(45 93% 47% / 0.15)", color: "#f59e0b", icon: "mdi:progress-clock" },
      "WAITING FOR DETAIL": { bg: "hsl(262 83% 58% / 0.15)", color: "#a855f7", icon: "mdi:clock-outline" },
      COMPLETED: { bg: "hsl(142 76% 36% / 0.15)", color: "#059669", icon: "mdi:check-circle" },
      CLOSED: { bg: "hsl(0 84% 60% / 0.15)", color: "#ef4444", icon: "mdi:close-circle" },
    };
    return statusColors[status] || statusColors.OPEN;
  };

  const getPriorityColor = (priority) => {
    const priorityColors = {
      LOW: { bg: "hsl(142 76% 36% / 0.15)", color: "#059669", icon: "mdi:arrow-down" },
      MEDIUM: { bg: "hsl(45 93% 47% / 0.15)", color: "#f59e0b", icon: "mdi:minus" },
      HIGH: { bg: "hsl(0 84% 60% / 0.15)", color: "#ef4444", icon: "mdi:arrow-up" },
    };
    return priorityColors[priority] || priorityColors.MEDIUM;
  };

  if (isLoadingPage) return <PageLoader />;
  if (!ticket) return null;

  const statusStyle = getStatusColor(ticket.status);
  const priorityStyle = getPriorityColor(ticket.priority);

  return (
    <div className="login-page container">
      <div className="d-flex justify-content-center">
        <div className="container mx-auto" style={{ maxWidth: "1200px" }}>
          {/* Header Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <button
                type="button"
                className="cancel-button"
                onClick={handleBack}
              >
                <Icon icon="mdi:arrow-left" /> Back
              </button>
              <button
                type="button"
                className="theme-button"
                onClick={handleEdit}
              >
                <Icon icon="mdi:pencil" /> Edit Ticket
              </button>
            </div>
          </div>

          {/* Main Ticket Card */}
          <div className="theme-card mb-4" style={{ padding: "24px" }}>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
              <div className="flex-1" style={{ minWidth: "200px" }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Icon icon="mdi:ticket-confirmation" width="24" style={{ color: "var(--primary-color)" }} />
                  <h3 className="mb-0" style={{ fontSize: "1.5rem", fontWeight: "600" }}>
                    {ticket.ticket_title || "Untitled Ticket"}
                  </h3>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: "0.875rem", marginTop: "4px" }}>
                  Ticket ID: <strong>#{ticket.ticket_id || "N/A"}</strong>
                </p>
              </div>
              <div className="d-flex gap-2 flex-wrap" style={{ alignItems: "flex-start" }}>
                {/* Status Badge */}
                <div
                  style={{
                    backgroundColor: statusStyle.bg,
                    color: statusStyle.color,
                    padding: "8px 16px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    transition: "transform 0.2s",
                    cursor: "default",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <Icon icon={statusStyle.icon} width="18" />
                  {ticket.status}
                </div>
                {/* Priority Badge */}
                <div
                  style={{
                    backgroundColor: priorityStyle.bg,
                    color: priorityStyle.color,
                    padding: "8px 16px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    transition: "transform 0.2s",
                    cursor: "default",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <Icon icon={priorityStyle.icon} width="18" />
                  {ticket.priority} Priority
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="mb-4">
              <h5 className="mb-3 d-flex align-items-center gap-2" style={{ fontSize: "1.1rem", fontWeight: "600" }}>
                <Icon icon="mdi:text-box-outline" width="20" style={{ color: "var(--primary-color)" }} />
                Description
              </h5>
              <div
                className="description-box"
                style={{
                  padding: "16px",
                  backgroundColor: "var(--muted-bg)",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  minHeight: "80px",
                  whiteSpace: "pre-wrap",
                  lineHeight: "1.6",
                  color: "var(--text-color)",
                }}
              >
                {ticket.ticket_description || "No description provided."}
              </div>
            </div>

            {/* Ticket Information Grid */}
            <div className="row g-3">
              <div className="col-md-6">
                <div style={{ padding: "12px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", color: "var(--dark-text-muted)", marginBottom: "6px", fontWeight: "500" }}>
                    <Icon icon="mdi:calendar-plus" width="18" />
                    Created At
                  </div>
                  <div style={{ fontSize: "1rem", color: "var(--text-color)", fontWeight: "500" }}>
                    {ticket.created_at
                      ? moment(ticket.created_at).format("DD MMM YYYY, hh:mm A")
                      : "N/A"}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{ padding: "12px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", color: "var(--dark-text-muted)", marginBottom: "6px", fontWeight: "500" }}>
                    <Icon icon="mdi:calendar-clock" width="18" />
                    Last Updated
                  </div>
                  <div style={{ fontSize: "1rem", color: "var(--text-color)", fontWeight: "500" }}>
                    {ticket.updated_at
                      ? moment(ticket.updated_at).format("DD MMM YYYY, hh:mm A")
                      : ticket.created_at
                      ? moment(ticket.created_at).format("DD MMM YYYY, hh:mm A")
                      : "N/A"}
                  </div>
                </div>
              </div>
              {ticket.Category && (
                <div className="col-md-6">
                  <div style={{ padding: "12px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", color: "var(--dark-text-muted)", marginBottom: "6px", fontWeight: "500" }}>
                      <Icon icon="mdi:folder-outline" width="18" />
                      Category
                    </div>
                    <div style={{ fontSize: "1rem", color: "var(--text-color)", fontWeight: "500" }}>{ticket.Category}</div>
                  </div>
                </div>
              )}
              {ticket.assignee && (
                <div className="col-md-6">
                  <div style={{ padding: "12px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", color: "var(--dark-text-muted)", marginBottom: "6px", fontWeight: "500" }}>
                      <Icon icon="mdi:account-tie" width="18" />
                      Assignee
                    </div>
                    <div style={{ fontSize: "1rem", color: "var(--text-color)", fontWeight: "500" }}>{ticket.assignee}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Updates Section */}
          <div className="theme-card mb-4" style={{ padding: "24px" }}>
            <h5 className="mb-4 d-flex align-items-center gap-2" style={{ fontSize: "1.1rem", fontWeight: "600" }}>
              <Icon icon="mdi:update" width="20" style={{ color: "var(--primary-color)" }} />
              Updates
            </h5>
            {ticket.updates && ticket.updates.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {ticket.updates.map((update, index) => (
                  <div
                    key={update.update_id || index}
                    className="description-box"
                    style={{
                      padding: "16px",
                      backgroundColor: "var(--muted-bg)",
                      borderRadius: "8px",
                      border: "1px solid var(--border-color)",
                      whiteSpace: "pre-wrap",
                      lineHeight: "1.6",
                      color: "var(--text-color)",
                    }}
                  >
                    <div style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid var(--border-color)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", color: "var(--dark-text-muted)" }}>
                          <Icon icon="mdi:account-circle" width="18" />
                          <span style={{ fontWeight: "500" }}>{update.updated_by || "Partner"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", color: "var(--dark-text-muted)" }}>
                          <Icon icon="mdi:clock-outline" width="18" />
                          <span>
                            {update.created_at
                              ? moment(update.created_at).format("DD MMM YYYY, hh:mm A")
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                      {update.description || update.newupdate || ""}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "var(--muted-bg)",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  textAlign: "center",
                  color: "var(--dark-text-muted)",
                  fontStyle: "italic",
                }}
              >
                No updates available yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
